import { afterNextRender, DestroyRef, Injectable, Injector } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Portal } from '@angular/cdk/portal';
import { OuiSlidePanelService } from 'omnium-ui/layout';
import { BehaviorSubject, Subject, filter } from 'rxjs';
import { BoxFile, FichierOperation, FileMetadata } from '../../../models/generated/graphql';
import {
  AppendedDocumentViewerRemoveEvent,
  AppendedDocumentViewerResult,
} from '../appended-document-viewer/appended-document-viewer.component';

export type DocumentViewerInfo = {
  viewerType: 'custom' | 'simple' | 'appended-file' | 'operation';
  fileMetadata?: FileMetadata;
  boxFile?: BoxFile;
  commentaire?: string;
  documentUrl?: string;
  fichierOperation?: FichierOperation;
  customPortal?: Portal<any>;
  onCloseAppendedFileViewer?: (result: AppendedDocumentViewerResult | undefined) => void;
  onRemoveFile?: (event: AppendedDocumentViewerRemoveEvent) => void;
};

export type SimpleDocumentViewerInfo = {
  fileMetadata?: FileMetadata;
  boxFile?: BoxFile;
  commentaire?: string;
  documentUrl?: string;
};

@Injectable({
  providedIn: 'root',
})
export class BoxDocumentViewerService {
  private _fileViewerInfoSubject = new BehaviorSubject<DocumentViewerInfo | null>(null);
  public readonly fileViewerInfo$ = this._fileViewerInfoSubject.asObservable();

  private readonly viewerClosedSubject = new Subject<void>();
  public readonly viewerClosed$ = this.viewerClosedSubject.asObservable();

  private opened: boolean = false;

  slidePanelContent?: Portal<any>;
  customSlidePanelContent?: Portal<any>;

  constructor(private slidePanelService: OuiSlidePanelService, private injector: Injector, destroyRef: DestroyRef) {
    this.slidePanelService.slidePanelState$.pipe(
      // The DS observable can replay 'closed' before its opening signal is propagated.
      // Check the current signal as well to avoid clearing a viewer that is opening.
      filter(state => state === 'closed' && this.opened && this.slidePanelService.slidePanelState() === 'closed'),
      takeUntilDestroyed(destroyRef)
    ).subscribe(() => this.onPanelClose());
  }

  public setDocumentViewerPortal(portal: Portal<any>) {
    this.slidePanelContent = portal;
  }

  private openDocumentViewer() {
    if (!this.slidePanelContent) {
      throw "Impossible d'ouvrir le document viewer : portal non défini. Définissez le à l'aide de la méthode setDocumentViewerPortal.";
    }

    this.slidePanelService.setSlidePanelContent(this.slidePanelContent);
    this.slidePanelService.openSlidePanel({ position: 'right', autoFocus: false, disableClose: true });

    this.opened = true;
  }

  public openCustomDocumentViewer(portal: Portal<any>) {
    this._fileViewerInfoSubject.next({
      viewerType: 'custom',
      customPortal: portal,
    });

    this.openDocumentViewer();
  }

  public closeDocumentViewer() {
    this.slidePanelService.closeSlidePanel();
  }

  private onPanelClose() {
    this.opened = false;
    // Let MatSidenav process [opened]=false and remove inert from the page first.
    afterNextRender(() => {
      if (!this.opened && this.slidePanelService.slidePanelState() === 'closed') {
        this.slidePanelService.setSlidePanelContent(null);
        this.viewerClosedSubject.next();
      }
    }, { injector: this.injector });
  }

  public openSimpleDocumentViewer(fileInfo: SimpleDocumentViewerInfo): void {
    this._fileViewerInfoSubject.next({
      viewerType: 'simple',
      boxFile: fileInfo.boxFile,
      commentaire: fileInfo.commentaire,
      documentUrl: fileInfo.documentUrl,
      fileMetadata: fileInfo.fileMetadata,
    });

    this.openDocumentViewer();
  }

  public openAppendedDocumentViewer(
    fichierOperation: FichierOperation,
    onClose: (result: AppendedDocumentViewerResult | undefined) => void,
    onRemoveFile: (event: AppendedDocumentViewerRemoveEvent) => void
  ) {
    this._fileViewerInfoSubject.next({
      viewerType: 'appended-file',
      fichierOperation: fichierOperation,
      onCloseAppendedFileViewer: onClose,
      onRemoveFile: onRemoveFile,
    });

    this.openDocumentViewer();
  }

  public closeAppendedDocumentViewer(result: AppendedDocumentViewerResult | undefined) {
    this._fileViewerInfoSubject.value?.onCloseAppendedFileViewer?.(result);
    this.closeDocumentViewer();
  }

  public openOperationDocumentViewer(fichierOperation: FichierOperation) {
    this._fileViewerInfoSubject.next({
      viewerType: 'operation',
      fichierOperation: fichierOperation,
    });

    this.openDocumentViewer();
  }

  public removeFichierOperation(event: AppendedDocumentViewerRemoveEvent) {
    this._fileViewerInfoSubject.value?.onRemoveFile?.(event);
    this.closeDocumentViewer();
  }
}
